import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Speaker, Disc3, Mic, Layers, Lightbulb, Sparkles, MonitorPlay } from "lucide-react";
import { Reveal } from "./Reveal";
import Seo from "./Seo";

const GEAR = [
  {
    icon: Disc3,
    brand: "Pioneer DJ",
    title: "Régie DJ",
    text: "Régie Pioneer DJ : la référence des DJ professionnels pour des transitions précises et un son irréprochable, du vin d'honneur au bout de la nuit.",
  },
  {
    icon: Speaker,
    brand: "Yamaha",
    title: "Sonorisation haute fidélité",
    text: "Sonorisation Yamaha : un son clair et puissant, calibré selon votre salle ou votre extérieur, pour le cocktail comme pour la piste de danse.",
  },
  {
    icon: Mic,
    brand: "Sennheiser",
    title: "Micros sans fil",
    text: "Micros HF Sennheiser haute portée : discours, cérémonie laïque et animations entendus parfaitement par tous vos invités, partout.",
  },
  {
    icon: Layers,
    brand: "Global Truss",
    title: "Structures professionnelles",
    text: "Structures Global Truss pour installer la régie et les éclairages proprement et en sécurité, en intérieur comme en extérieur.",
  },
  {
    icon: Lightbulb,
    brand: "LED & DMX",
    title: "Éclairage architectural",
    text: "Projecteurs LED sur batterie (aucun câble au sol) et programmation DMX personnalisée : votre salle change d'ambiance au fil de la soirée.",
  },
  {
    icon: Sparkles,
    brand: "Effets scéniques",
    title: "Étincelles froides & fumée lourde",
    text: "Étincelles froides non pyrotechniques pour l'ouverture de bal et la pièce montée, machine à fumée lourde pour un effet nuage saisissant.",
  },
  {
    icon: MonitorPlay,
    brand: "Vidéo",
    title: "Vidéoprojection",
    text: "Vidéoprojecteur avec écran pour vos diaporamas et surprises — vos fichiers sont testés en amont, transmis au plus tard 48 h avant l'événement.",
  },
];

export default function MaterielPage() {
  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="materiel-page">
      <Seo
        title="Notre Matériel | Pioneer DJ, Sennheiser, Yamaha, Global Truss — KLMProd"
        description="La régie KLMProd : Pioneer DJ, sonorisation Yamaha, micros Sennheiser, structures Global Truss, éclairage LED DMX, étincelles froides et fumée lourde. Du matériel professionnel pour vos mariages et événements."
        path="/materiel"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "KLMProd",
          url: "https://klmprod.com/materiel",
          email: "contact@klmprod.com",
        }}
      />

      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="materiel-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=1920&q=80&auto=format&fit=crop"
            alt=""
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/60 via-[#080C14]/70 to-[#080C14]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <Reveal>
            <p className="font-mono-brand text-xs uppercase tracking-[0.3em] text-cyan-400">Notre matériel</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Une régie professionnelle, <span className="text-gradient">rien d'autre.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Un bel événement repose sur du matériel fiable. Voici l'équipement que nous installons
              sur chaque prestation — les mêmes marques que sur les scènes professionnelles.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {GEAR.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.06} className="h-full">
              <div
                className="group h-full rounded-3xl border border-cyan-500/15 bg-[#0E1524]/70 p-7 backdrop-blur-xl transition-colors duration-500 hover:border-cyan-400/40"
                data-testid={`gear-card-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/25 bg-cyan-400/10 transition-transform duration-500 group-hover:scale-110">
                    <g.icon size={22} className="text-cyan-300" />
                  </div>
                  <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-cyan-400/80">{g.brand}</span>
                </div>
                <h2 className="font-display mt-5 text-xl font-bold text-slate-100">{g.title}</h2>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{g.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-[2rem] border border-cyan-400/20 bg-gradient-to-br from-[#0E1524] via-[#0B1E33] to-[#0E1524] p-10 text-center sm:p-14">
            <h2 className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">
              Voyez ce matériel <span className="text-gradient">en action.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Chaque formule détaille précisément le matériel installé chez vous. Devis gratuit,
              réponse sous 48 h.
            </p>
            <Link
              to="/#devis"
              data-testid="materiel-cta-devis"
              className="bg-brand-gradient group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-105"
            >
              Demander un devis
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
